import { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Camera, X, Loader2, RefreshCw } from 'lucide-react';

interface DeliveryPhotoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (file: File) => Promise<void>;
  isLoading: boolean;
}

export const DeliveryPhotoDialog = ({
  open,
  onOpenChange,
  onConfirm,
  isLoading,
}: DeliveryPhotoDialogProps) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const startCamera = async () => {
    setCameraError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
        audio: false,
      });
      
      streamRef.current = stream;
      setIsCameraActive(true);
    } catch (error) {
      console.error('Camera access error:', error);
      setCameraError('Unable to access camera. Please allow camera permissions.');
    }
  };

  // Connect stream to video element after it renders
  useEffect(() => {
    if (isCameraActive && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(console.error);
    }
  }, [isCameraActive]);

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setIsCameraActive(false);
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    ctx.drawImage(video, 0, 0);
    
    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], `delivery-photo-${Date.now()}.jpg`, {
          type: 'image/jpeg',
        });
        setSelectedFile(file);
        setPreview(canvas.toDataURL('image/jpeg'));
        stopCamera();
      }
    }, 'image/jpeg', 0.9);
  };

  const handleRetake = () => {
    setSelectedFile(null);
    if (preview) {
      URL.revokeObjectURL(preview);
    }
    setPreview(null);
    startCamera();
  };

  const handleConfirm = async () => {
    if (selectedFile) {
      await onConfirm(selectedFile);
      handleClose();
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      stopCamera();
      setSelectedFile(null);
      if (preview) {
        URL.revokeObjectURL(preview);
      }
      setPreview(null);
      setCameraError(null);
      onOpenChange(false);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  // Stop camera when dialog closes
  useEffect(() => {
    if (!open) {
      stopCamera();
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Camera className="h-5 w-5" />
            Take Delivery Photo
          </DialogTitle>
          <DialogDescription>
            Take a photo showing the delivery at the gravesite
          </DialogDescription>
        </DialogHeader>

        {/* Hidden canvas for capturing */}
        <canvas ref={canvasRef} className="hidden" />

        <div className="space-y-4">
          {/* Initial state - Start Camera button */}
          {!isCameraActive && !preview && !cameraError && (
            <Button
              variant="outline"
              className="w-full h-32 flex flex-col gap-2"
              onClick={startCamera}
              disabled={isLoading}
            >
              <Camera className="h-10 w-10 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                Tap to open camera
              </span>
            </Button>
          )}

          {/* Camera error state */}
          {cameraError && (
            <div className="text-center p-4 border border-destructive/50 rounded-lg bg-destructive/10">
              <p className="text-sm text-destructive mb-3">{cameraError}</p>
              <Button variant="outline" size="sm" onClick={startCamera}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Try Again
              </Button>
            </div>
          )}

          {/* Live camera feed */}
          {isCameraActive && !preview && (
            <div className="space-y-3">
              <div className="relative rounded-lg overflow-hidden bg-black">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-64 object-cover"
                />
              </div>
              <Button
                className="w-full"
                onClick={capturePhoto}
                disabled={isLoading}
              >
                <Camera className="h-4 w-4 mr-2" />
                Capture Photo
              </Button>
            </div>
          )}

          {/* Photo preview */}
          {preview && (
            <div className="relative">
              <img
                src={preview}
                alt="Delivery preview"
                className="w-full h-64 object-cover rounded-lg"
              />
              <Button
                variant="destructive"
                size="icon"
                className="absolute top-2 right-2 h-8 w-8"
                onClick={handleRetake}
                disabled={isLoading}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          )}

          <div className="flex gap-3">
            <Button
              variant="outline"
              className="flex-1"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              className="flex-1"
              onClick={handleConfirm}
              disabled={!selectedFile || isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                'Complete Delivery'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
